class BurdHomeNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.menu = this.querySelector('[data-home-menu]');
    this.search = this.querySelector('[data-home-search]');
    this.drawer = this.querySelector('.burd-nav__drawer');
    this.trigger = this.menu.querySelector('summary');
    for (const button of this.querySelectorAll('button[hidden]')) button.hidden = false;
    for (const close of this.querySelectorAll('[data-menu-dismiss]')) close.addEventListener('click', () => { this.menu.open = false; }, options);
    this.querySelector('[data-search-dismiss]').addEventListener('click', () => { this.search.open = false; }, options);
    this.menu.addEventListener('toggle', () => this.toggleMenu(), options);
    this.search.addEventListener('toggle', () => {
      if (this.search.open) { this.menu.open = false; this.querySelector('input[type="search"]').focus(); }
      else if (this.search.contains(document.activeElement)) this.search.querySelector('summary').focus();
    }, options);
    document.addEventListener('keydown', event => this.handleKey(event), options);
    const bag = this.querySelector('[data-cart-toggle]');
    bag?.addEventListener('click', event => {
      if (!document.querySelector('[data-cart-drawer]') || !window.theme) return;
      event.preventDefault();
      this.search.open = false;
      document.dispatchEvent(new CustomEvent('theme:cart:toggle', { bubbles: true }));
    }, options);
    document.addEventListener('theme:cart:open', () => bag?.setAttribute('aria-expanded', 'true'), options);
    document.addEventListener('theme:cart:close', () => bag?.setAttribute('aria-expanded', 'false'), options);
    this.updateScrolled = () => this.toggleAttribute('data-scrolled', window.scrollY > (document.querySelector('.burd-home__hero')?.offsetHeight || 600) - 80);
    window.addEventListener('scroll', this.updateScrolled, { ...options, passive: true });
    this.updateScrolled();
  }
  toggleMenu() {
    this.trigger.setAttribute('aria-expanded', String(this.menu.open));
    if (this.menu.open) {
      this.menuWasOpen = true;
      this.search.open = false;
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.inerted = [];
      // Inert siblings along the menu's ancestor chain, including header controls.
      // Preserve nodes that another overlay already made inert.
      for (let branch = this.menu; branch && branch !== document.body; branch = branch.parentElement) {
        for (const element of branch.parentElement?.children || []) {
          if (element === branch || element.matches('script, style, link') || element.inert) continue;
          element.inert = true;
          this.inerted.push(element);
        }
      }
      this.drawer.focus();
    } else {
      this.drawer.removeAttribute('aria-modal');
      this.drawer.removeAttribute('role');
      for (const element of this.inerted || []) element.inert = false;
      this.inerted = [];
      if (this.menuWasOpen && !this.search.open) this.trigger.focus();
      this.menuWasOpen = false;
    }
  }
  handleKey(event) {
    if (event.key === 'Escape') {
      if (this.menu.open) { event.preventDefault(); this.menu.open = false; }
      if (this.search.open) { event.preventDefault(); this.search.open = false; }
      return;
    }
    if (event.key !== 'Tab' || !this.menu.open) return;
    const items = [...this.drawer.querySelectorAll('a[href], button, summary, input')].filter(item => item.getClientRects().length && !item.disabled);
    const first = items[0], last = items.at(-1);
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.drawer)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === this.drawer)) { event.preventDefault(); first?.focus(); }
  }
  disconnectedCallback() {
    this.controller?.abort();
    for (const element of this.inerted || []) element.inert = false;
  }
}
class BurdProductMotion extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.reduced.matches || this.dataset.autoplay !== 'true';
    this.hovered = false;
    this.focused = false;
    this.visible = false;
    this.button = this.querySelector('[data-motion-toggle]');
    this.rows = [...this.querySelectorAll('.burd-motion__row')];
    this.positions = this.rows.map(() => 0);
    for (const row of this.rows) {
      const clone = row.querySelector('.burd-motion__group').cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      for (const anchor of clone.querySelectorAll('a')) anchor.tabIndex = -1;
      row.querySelector('.burd-motion__track').append(clone);
      row.setAttribute('data-enhanced', '');
      row.addEventListener('focusin', event => {
        if (event.target.matches('a')) event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }, options);
      row.addEventListener('pointerdown', () => { this.paused = true; this.updateButton(); }, options);
      row.addEventListener('wheel', () => { this.paused = true; this.updateButton(); }, { ...options, passive: true });
      row.addEventListener('scroll', () => { this.positions[this.rows.indexOf(row)] = row.scrollLeft; }, { ...options, passive: true });
    }
    this.querySelector('.burd-motion__controls').hidden = false;
    this.button.addEventListener('click', () => { this.paused = !this.paused; this.updateButton(); }, options);
    this.querySelector('[data-motion-previous]').addEventListener('click', () => this.step(-1), options);
    this.querySelector('[data-motion-next]').addEventListener('click', () => this.step(1), options);
    this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.hovered = true; }, options);
    this.addEventListener('pointerleave', () => { this.hovered = false; }, options);
    this.addEventListener('focusin', event => { this.focused = !event.target.closest('.burd-motion__controls'); }, options);
    this.addEventListener('focusout', () => queueMicrotask(() => { this.focused = this.contains(document.activeElement) && !document.activeElement.closest('.burd-motion__controls'); }), options);
    this.reduced.addEventListener('change', () => { this.paused = true; this.updateButton(); }, options);
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
    this.observer.observe(this);
    this.updateButton();
    this.tick = time => {
      const elapsed = Math.min((time - (this.lastTime || time)) / 1000, .05);
      this.lastTime = time;
      if (this.visible && !document.hidden && !this.paused && !this.hovered && !this.focused) {
        for (const [index, row] of this.rows.entries()) {
          const width = row.querySelector('.burd-motion__group').offsetWidth;
          if (width <= row.clientWidth) continue;
          const direction = index === 1 ? -1 : 1;
          this.positions[index] = (this.positions[index] + direction * elapsed * Number(this.dataset.speed) + width) % width;
          row.scrollLeft = this.positions[index];
        }
      }
      this.frame = requestAnimationFrame(this.tick);
    };
    this.frame = requestAnimationFrame(this.tick);
  }
  updateButton() {
    this.button.textContent = this.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
    this.button.setAttribute('aria-pressed', String(this.paused));
    this.dataset.motionPaused = String(this.paused);
  }
  step(direction) {
    this.paused = true;
    this.updateButton();
    for (const row of this.rows) row.scrollBy({ left: direction * row.clientWidth * .65, behavior: this.reduced.matches ? 'instant' : 'smooth' });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    for (const clone of this.querySelectorAll('.burd-motion__group[aria-hidden]')) clone.remove();
  }
}
if (!customElements.get('burd-home-navigation')) customElements.define('burd-home-navigation', BurdHomeNavigation);
if (!customElements.get('burd-product-motion')) customElements.define('burd-product-motion', BurdProductMotion);
